<template>
  <div class="automations-v2 w-full pb-12">
    <ConfirmDialog />

    <div v-if="loading" class="flex justify-center items-center py-12">
      <loader />
    </div>

    <div v-else class="flex flex-col gap-3 w-full px-4 md:px-0">
      <!-- Empty state banner (no active automations) -->
      <div v-if="activeCount === 0" class="intro-banner">
        <div class="banner-circle" />
        <div class="banner-icon">
          <i class="pi pi-sparkles text-2xl"></i>
        </div>
        <div class="flex-1 relative">
          <div class="text-lg font-medium">{{ automationTypesConfig.length }} אוטומציות מוכנות בשבילך</div>
          <div class="text-[13px] opacity-90 mt-1 leading-relaxed">
            כל אוטומציה כבר מוכנה עם טקסט מומלץ — רק הפעל את מה שמתאים לעסק שלך.
          </div>
        </div>
      </div>

      <!-- Automation cards grid -->
      <div class="cards-grid">
        <div
          v-for="meta in automationTypesConfig"
          :key="meta.type"
          class="automation-card"
          :class="{ 'coming-soon': meta.comingSoon }"
          @click="!meta.comingSoon && openEditDrawer(meta.type)"
        >
          <div class="flex items-start gap-3">
            <div
              class="icon-box"
              :style="{ backgroundColor: meta.color + '18', color: meta.color }"
            >
              <i :class="['pi', meta.icon]"></i>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="font-medium text-[15px]">{{ meta.name }}</span>
                <div v-if="!meta.comingSoon && (meta.alwaysOn || (getConfig(meta.type)?.enabled ?? meta.defaultOn))" class="live-dot" />
                <span v-if="meta.comingSoon" class="coming-soon-badge">בקרוב</span>
              </div>
              <div class="text-xs text-surface-500 mt-0.5">{{ meta.description }}</div>
            </div>
            <InputSwitch
              v-if="!meta.alwaysOn && !meta.comingSoon"
              :modelValue="getConfig(meta.type)?.enabled ?? meta.defaultOn ?? false"
              @update:modelValue="handleToggle(meta.type, $event)"
              @click.stop
            />
          </div>

          <!-- Footer row — channel + trigger summary -->
          <div v-if="!meta.comingSoon" class="card-footer">
            <div class="flex items-center gap-1.5 text-surface-600">
              <i :class="[
                'pi text-[14px]',
                getConfig(meta.type)?.channel === 'whatsapp' ? 'pi-whatsapp text-green-700' : 'pi-envelope text-primary-600'
              ]"></i>
              <span>{{ getConfig(meta.type)?.channel === 'whatsapp' ? 'WhatsApp' : 'SMS' }}</span>
            </div>
            <div class="footer-divider" />
            <div class="flex items-center gap-1.5 text-surface-600">
              <i class="pi pi-clock text-[13px]"></i>
              <span>{{ getWhenLabel(meta) }}</span>
            </div>
            <div class="flex-1" />
            <span v-if="meta.alwaysOn" class="text-primary-600 text-xs font-medium">
              הגדרות
            </span>
          </div>
        </div>
      </div>

      <!-- Legacy automations section -->
      <div v-if="legacyAutomations.length > 0" class="mt-6">
        <Divider align="center">
          <span class="text-sm text-surface-400">אוטומציות ישנות</span>
        </Divider>
        <p class="text-xs text-surface-400 mb-2 text-center">
          אוטומציות שנוצרו במערכת הישנה. מומלץ להעביר אותן למערכת החדשה.
        </p>
        <row-card
          v-for="automation in legacyAutomations"
          :key="automation.id"
          :title="automation.name"
          icon="pi pi-bolt"
          :content="getLegacyContent(automation)"
          showButton
          buttonIcon="pi-trash"
          @click="goToLegacyAutomation(automation)"
          @buttonClick="removeLegacyAutomation(automation)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ISmsAutomation, IAutomationConfig, IAutomationTypeMeta, TSettingsScheduling } from '@torim-app/types';
import {
  ALLOW_TO_ENTER_WAITING_LIST_MAX,
  AutomationTypeEnum,
  AutomationChannelEnum,
  InvoiceProvider,
  ClientFeatures
} from '@torim-app/types';
import { checkFeatureIsAvailable } from '@torim-app/utils';
import { computed, onMounted, ref } from 'vue';
import { CommunicationApi } from '@app/api/admin';
import { useAdminAutomations, useAdminStore } from '@app/stores';
import { useRouter } from 'vue-router';
import { AdminRouteNames } from '@app/router/route-names';
import { useToast } from 'vue-toastification';
import { useConfirm } from 'primevue/useconfirm';
import Divider from 'primevue/divider';
import ConfirmDialog from 'primevue/confirmdialog';
import { automationTypesConfig as allAutomationTypesConfig, automationTypesMeta, createDefaultConfig, getDefaultMessage } from './automation-types-config';
import { triggersMapping } from './automations-consts';
import { trackEvent } from '@app/composables/posthog';
import { useDrawer } from '@torim-app/shared-components';
import EditAutomationDrawer from './EditAutomationDrawer.vue';
import { SettingsApi } from '@app/api/admin';

const toast = useToast();
const confirm = useConfirm();
const router = useRouter();
const adminStore = useAdminStore();
const automationsStore = useAdminAutomations();
const { openDrawer } = useDrawer();

const loading = ref(true);
const configs = ref<IAutomationConfig[]>([]);

const hasWhatsApp = computed(() => !!adminStore.businessSiteData?.whatsappIntegration?.accessToken);
const hasLoyalty = computed(() => !!adminStore.businessSiteData?.loyaltyProgram);
const SUPPORTED_INVOICE_PROVIDERS = [InvoiceProvider.PAYME, InvoiceProvider.SMARTBEE];
const hasSupportedInvoiceProvider = computed(() =>
  SUPPORTED_INVOICE_PROVIDERS.includes(
    adminStore.businessSiteData?.paymentIntegration?.invoiceConfig?.provider as InvoiceProvider
  )
);
const hasSkinFeature = computed(() =>
  adminStore.businessSiteData?.featuresConfigurations?.[ClientFeatures.SkinAnalysis]?.enabled === true
);
const hasShop = computed(() =>
  checkFeatureIsAvailable(ClientFeatures.Shop, adminStore.businessSiteData?.plan)
);
const legacyAutomations = computed(() => automationsStore.getAutomations ?? []);

// Filter out hidden automation types + feature-gated ones the business can't use.
const automationTypesConfig = computed(() =>
  allAutomationTypesConfig.filter(m =>
    m.type !== AutomationTypeEnum.NoShowFollowUp &&
    (!m.requiresSkinFeature || hasSkinFeature.value) &&
    (!m.requiresShop || hasShop.value)
  )
);

const activeCount = computed(() => {
  return configs.value.filter(c => c.enabled).length;
});

const getConfig = (type: AutomationTypeEnum): IAutomationConfig | undefined => {
  return configs.value.find(c => c.type === type);
};

const getWhenLabel = (meta: IAutomationTypeMeta): string => {
  const config = getConfig(meta.type);
  const s = config?.settings || {};

  switch (meta.type) {
    case 'appointment-reminder': {
      const days = s.sendReminderXDaysBefore ?? 1;
      return days === 1 ? 'יום לפני התור' : `${days} ימים לפני התור`;
    }
    case 'abandoned-scheduling': {
      const mins = s.timeoutMinutes ?? 10;
      return `${mins} דקות אחרי נטישה`;
    }
    case 'inactive-customer': {
      const days = s.inactiveDays ?? 30;
      return `${days} יום ללא ביקור`;
    }
    case 'post-visit-follow-up': {
      const days = s.daysAfter ?? 7;
      return days === 1 ? 'יום אחרי הביקור' : `${days} ימים אחרי הביקור`;
    }
    case 'review-request': {
      const hours = s.hoursAfter ?? 3;
      return `${hours} שעות אחרי ביקור`;
    }
    case 'booking-confirmation': return 'כשלקוח קובע פגישה';
    case 'booking-canceled': return 'כשלקוח מבטל';
    case 'appointment-rescheduled': return 'כשהתור מעודכן';
    case 'birthday-greeting': return 'בוקר יום ההולדת';
    case 'smart-nudge': return 'מבוסס מחזור ביקורים';
    case 'no-show-follow-up': return 'שעה אחרי הפגישה';
    case 'new-customer-welcome': return 'אחרי ההזמנה הראשונה';
    case 'same-day-reminder': return 'בבוקר יום התור';
    case 'waiting-list-notification': {
      const days = s.waitingListMaxDays ?? ALLOW_TO_ENTER_WAITING_LIST_MAX;
      return `כשנמצא תור פנוי · עד ${days} ימים קדימה`;
    }
    case 'invoice-receipt': return 'מיד עם יצירת חשבונית';
    case 'skin-scan-reminder': {
      const days = s.frequencyDays ?? 14;
      return `כל ${days} ימים`;
    }
    default: return '';
  }
};

const loadConfigs = async () => {
  loading.value = true;
  try {
    const [configsData] = await Promise.all([
      CommunicationApi.fetchAutomationConfigs(),
      automationsStore.fetchAllAutomations(),
    ]);
    configs.value = configsData;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const saveConfig = async (config: IAutomationConfig) => {
  try {
    const result = await CommunicationApi.upsertAutomationConfig(config);
    const idx = configs.value.findIndex(c => c.type === config.type);
    if (idx >= 0) {
      configs.value[idx] = result;
    } else {
      configs.value.push(result);
    }
    // Keep the shared store in sync so getters like isCancellationAutomationEnabled
    // (read across the app, e.g. the cancel-appointment prompt) reflect this change
    // without needing a page refresh.
    await automationsStore.fetchAutomationConfigs();
  } catch (e) {
    toast.error('שמירת האוטומציה נכשלה');
    console.error(e);
  }
};

const enrichConfigSettings = (type: AutomationTypeEnum, config: IAutomationConfig) => {
  const bizConfig = adminStore.businessSiteData?.configurations;

  if (bizConfig) {
    if (type === AutomationTypeEnum.ReviewRequest) {
      if (!config.settings.googleMapsReviewsId) {
        config.settings.googleMapsReviewsId = bizConfig.googleMapsReviewsId || '';
      }
      if (config.settings.showReviewsOnWebsite === undefined) {
        config.settings.showReviewsOnWebsite = bizConfig.showReviewsOnWebsite ?? false;
      }
    }
    if (type === AutomationTypeEnum.AppointmentReminder) {
      if (!config.settings.sendReminderXDaysBefore) {
        config.settings.sendReminderXDaysBefore = bizConfig.sendReminderXDaysBefore ?? 1;
      }
      if (config.channel === AutomationChannelEnum.SMS && bizConfig.reminderSms === 'whatsapp') {
        config.channel = AutomationChannelEnum.WhatsApp;
      }
    }
    if (type === AutomationTypeEnum.WaitingListNotification) {
      if (config.channel === AutomationChannelEnum.SMS && bizConfig.waitingListSms === 'whatsapp') {
        config.channel = AutomationChannelEnum.WhatsApp;
      }
      // The window lives on business.configurations (the booking website reads it from
      // there), so seed the editor from it rather than from the automation document.
      if (!config.settings.waitingListMaxDays) {
        config.settings.waitingListMaxDays = bizConfig.waitingListMaxDays ?? ALLOW_TO_ENTER_WAITING_LIST_MAX;
      }
    }
    if (type === AutomationTypeEnum.SameDayReminder) {
      if (config.channel === AutomationChannelEnum.SMS && (bizConfig as any).sameDayReminderSms === 'whatsapp') {
        config.channel = AutomationChannelEnum.WhatsApp;
      }
    }
  }

  // Ensure SMS message is populated with the default
  if (!config.message) {
    config.message = getDefaultMessage(type, AutomationChannelEnum.SMS);
  }
};

const openEditDrawer = (type: AutomationTypeEnum) => {
  const meta = automationTypesMeta[type];
  let config = getConfig(type);
  if (!config) {
    config = createDefaultConfig(type);
  }
  const configCopy: IAutomationConfig = { ...config, settings: { ...config.settings } };
  if (meta.alwaysOn) configCopy.enabled = true;
  enrichConfigSettings(type, configCopy);
  openDrawer(EditAutomationDrawer, {
    config: configCopy,
    meta,
    hasWhatsApp: hasWhatsApp.value,
    hasLoyalty: hasLoyalty.value,
    hasSupportedInvoiceProvider: hasSupportedInvoiceProvider.value,
    onSave: async (updatedConfig: IAutomationConfig) => {
      await saveConfig(updatedConfig);
      await syncAutomationToConfigurations(updatedConfig);
      toast.success('האוטומציה נשמרה בהצלחה');
    },
  }, { title: meta.name });
};

const syncAutomationToConfigurations = async (config: IAutomationConfig) => {
  // Settings the booking website / cron read from business.configurations must be mirrored
  // there — the automation document alone isn't visible to them.
  const patch: TSettingsScheduling | null =
    config.type === AutomationTypeEnum.ReviewRequest
      ? {
        showReviewsOnWebsite: !!config.settings.showReviewsOnWebsite,
        googleMapsReviewsId: config.settings.googleMapsReviewsId || '',
      }
      : config.type === AutomationTypeEnum.WaitingListNotification
        ? { waitingListMaxDays: config.settings.waitingListMaxDays || ALLOW_TO_ENTER_WAITING_LIST_MAX }
        : null;
  if (!patch) return;
  try {
    await SettingsApi.updateSettingsScheduling(patch);
    await adminStore.fetchBusinessData();
  } catch (e) {
    console.error('Failed to sync automation settings to configurations', e);
  }
};

const handleToggle = async (type: AutomationTypeEnum, enabled: boolean) => {
  const meta = automationTypesMeta[type];
  if (meta.alwaysOn) return;
  trackEvent('automation_toggled', { type, enabled });
  if (enabled) {
    // When enabling, open the edit drawer
    let config = getConfig(type);
    if (!config) {
      config = createDefaultConfig(type);
    }
    config.enabled = true;
    const configCopy: IAutomationConfig = { ...config, settings: { ...config.settings } };
    enrichConfigSettings(type, configCopy);
    openDrawer(EditAutomationDrawer, {
      config: configCopy,
      meta,
      hasWhatsApp: hasWhatsApp.value,
      hasLoyalty: hasLoyalty.value,
      hasSupportedInvoiceProvider: hasSupportedInvoiceProvider.value,
      onSave: async (updatedConfig: IAutomationConfig) => {
        await saveConfig(updatedConfig);
        await syncAutomationToConfigurations(updatedConfig);
        toast.success('האוטומציה הופעלה בהצלחה');
      },
    }, { title: meta.name });
  } else {
    // When disabling, just save directly. For default-on automations there may be no
    // stored config yet, so create one (enabled=false) to persist the opt-out.
    let config = getConfig(type);
    if (!config) {
      config = createDefaultConfig(type);
    }
    config = { ...config, enabled: false };
    await saveConfig(config);
  }
};

const getLegacyContent = (automation: ISmsAutomation) => {
  const { steps } = automation;
  if (steps.length !== 3) return '';
  return `${triggersMapping[steps[1].action]?.name || ''} / ${triggersMapping[steps[2].action]?.name || ''}`;
};

const goToLegacyAutomation = (automation: ISmsAutomation) => {
  router.push({
    name: AdminRouteNames.Automations,
    params: { id: automation.id }
  });
};

const removeLegacyAutomation = (automation: ISmsAutomation) => {
  confirm.require({
    message: 'האם למחוק את האוטומציה הישנה? לא ניתן לבטל פעולה זו.',
    header: 'מחיקת אוטומציה',
    rejectLabel: 'לא',
    acceptLabel: 'כן, מחק',
    accept: async () => {
      try {
        if (automation.id)
          await CommunicationApi.removeAutomations(automation.id);
        await automationsStore.fetchAllAutomations();
        toast.success('האוטומציה נמחקה בהצלחה');
      } catch (e) {
        toast.error('מחיקת האוטומציה נכשלה');
      }
    },
  });
};

onMounted(() => {
  loadConfigs();
});
</script>

<style scoped lang="scss">
.automations-v2 {
  .intro-banner {
    background: linear-gradient(135deg, rgb(var(--primary)) 0%, #6831d0 100%);
    border-radius: 14px;
    padding: 16px 20px;
    margin-bottom: 8px;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 22px;
    position: relative;
    overflow: hidden;
  }

  .banner-circle {
    position: absolute;
    left: -20px;
    top: -20px;
    width: 180px;
    height: 180px;
    border-radius: 999px;
    background: rgba(255,255,255,0.08);
  }

  .banner-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: rgba(255,255,255,0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .cards-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .automation-card {
    background: var(--p-surface-0);
    border: 1px solid var(--p-surface-200);
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    cursor: pointer;
    transition: box-shadow 0.15s, border-color 0.15s;

    &:hover {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    }

    &.coming-soon {
      cursor: not-allowed;
      opacity: 0.7;

      &:hover {
        box-shadow: none;
      }
    }
  }

  .coming-soon-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    background: var(--p-surface-100);
    color: var(--p-surface-600);
    font-size: 10px;
    font-weight: 500;
  }

  .icon-box {
    width: 2.625rem;
    height: 2.625rem;
    border-radius: 0.625rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 1.25rem;
    }
  }

  .live-dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #15803d;
  }

  .card-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 10px;
    border-top: 1px solid var(--p-surface-100);
    font-size: 12px;
  }

  .footer-divider {
    width: 1px;
    height: 12px;
    background: var(--p-surface-200);
  }
}
</style>
